/*
 * 動作ログ（常設）
 *
 * 「利用者の環境でしか起きない」不具合を追えるようにするための仕組み。
 * iPhone の Safari は開発者ツールが使えず、PWA ではURLも変更できないため、
 * 利用者に操作をお願いせずに済むよう、記録と同時にサーバーへ自動送信する。
 *
 * 収集するもの:
 *  - 未捕捉の例外 / Promise の reject
 *  - console.error / console.warn
 *  - API のエラー（axiosClient から呼ぶ）
 *  - 画面遷移
 *  - 通知一覧の描画状態（DOMに要素があるのに見えないのかを判定するため）
 *
 * ログを膨らませないための方針:
 *  - 計測は毎回おこなうが、**正常と断定できる結果はサーバーへ送らない**
 *  - 異常を検知したときだけ、その前後の記録もまとめて送る
 *  - 同じ記録を二重に送らない（sent フラグ）
 *
 * ※ 個人情報は記録しない。件数・サイズ・座標などの数値のみを扱う。
 * ※ サーバー側の保持期間は nginx の logrotate に従う。
 */

export type DebugLogLevel = "error" | "warn" | "info" | "measure";

export interface DebugLogEntry {
  time: string;
  level: DebugLogLevel;
  tag: string;
  message: string;
  /** サーバーへ送信済みか。同じログを前後関係として何度も送らないための印 */
  sent?: boolean;
}

const MAX_ENTRIES = 300;
const STORAGE_KEY = "__livelet_debug_logs";
const ENABLED_KEY = "__livelet_debug_on";

/*
 * 「記録すること」と「画面に出すこと」を分けている。
 *
 * 記録・送信は常に有効:
 *   利用者の端末でしか起きない不具合を追うため。PWA では URL を変更できず、
 *   利用者に操作をお願いすることもできないので、条件を付けると証拠が残らない。
 *   画面には何も表示されないので、利用者から見た変化はない。
 *
 * パネル（LOGボタン）の表示だけが条件つき:
 *   1. ビルド時に REACT_APP_DEBUG_PANEL=1 が指定されている（dev環境）
 *   2. URLに ?debug=1 が付いている（調査時に手元で中身を見たいとき）
 *   2の場合、SPAの画面遷移でクエリが消えるため sessionStorage に印を残して維持する。
 */
const resolvePanelVisible = (): boolean => {
  if (process.env.REACT_APP_DEBUG_PANEL === "1") return true;
  try {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    if (params.get("debug") === "1") {
      sessionStorage.setItem(ENABLED_KEY, "1");
      return true;
    }
    return sessionStorage.getItem(ENABLED_KEY) === "1";
  } catch (e) {
    // プライベートブラウズ等で sessionStorage が使えなくても落とさない
    return false;
  }
};

const panelVisible = resolvePanelVisible();

/** ログを記録・送信するか。常に有効（画面には何も出ない） */
export const isDebugEnabled = (): boolean => true;

/** 画面にLOGボタンとログ一覧を出すか */
export const isPanelVisible = (): boolean => panelVisible;

let entries: DebugLogEntry[] = [];
let listeners: Array<() => void> = [];

const notify = () => listeners.forEach((fn) => fn());

const nowLabel = () => {
  const d = new Date();
  const p = (n: number, w = 2) => String(n).padStart(w, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}`;
};

const persist = () => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
  } catch (e) {
    // 保存できなくてもログ自体は続ける
  }
};

/*
 * サーバーへの自動送信
 *
 * 利用者に操作をお願いしなくて済むよう、記録と同時にサーバーへ送る。
 * 送り先は実在しないパスで、nginx のアクセスログに URL として残ることだけが目的
 * （404 が返るが送信自体は行われる）。このためサーバー側の実装は一切不要。
 *
 * - 画像リクエストとして投げるので CORS も待ち時間も発生しない
 * - 送信の失敗は完全に無視する（ここで例外を出すと画面を壊すため）
 * - 個人情報は送らない。送るのは件数・座標・エラー文言のみ
 */
const BEACON_PATH = "/__livelet-debug";
const MAX_URL_PAYLOAD = 1200;

// 同一セッションを追跡するための短いID（個人を特定する情報ではない）
const sessionTag = Math.random().toString(36).slice(2, 8);
let seq = 0;

const sendToServer = (entry: DebugLogEntry) => {
  try {
    seq += 1;
    const payload = `${entry.time}|${entry.level}|${entry.tag}|${entry.message}`.slice(0, MAX_URL_PAYLOAD);
    const url = `${BEACON_PATH}?s=${sessionTag}&n=${seq}&d=${encodeURIComponent(payload)}`;
    const img = new Image();
    img.onerror = () => undefined; // 404 は想定どおりなので握りつぶす
    img.onload = () => undefined;
    img.src = url;
  } catch (e) {
    // 送信できなくても画面の動作には影響させない
  }
};

/**
 * ログを記録する。
 *
 * @param send false を渡すとサーバーへ送らず手元にだけ残す。
 *             「正常だった計測結果」を送らないために使う（常設運用でログを膨らませないため）。
 */
export const debugLog = (level: DebugLogLevel, tag: string, message: string, send = true) => {
  if (!isDebugEnabled()) return;
  const entry: DebugLogEntry = { time: nowLabel(), level, tag, message };
  entries.push(entry);
  if (entries.length > MAX_ENTRIES) entries = entries.slice(-MAX_ENTRIES);
  persist();
  if (send) {
    entry.sent = true;
    sendToServer(entry);
  }
  notify();
};

/**
 * 異常を検知したときに、直前の状況もまとめてサーバーへ送る。
 * 正常時は送っていないため、これが無いと異常の前後関係が分からなくなる。
 */
const sendRecentContext = (count = 5) => {
  entries.slice(-(count + 1), -1).forEach((e) => {
    if (e.sent) return; // 既に送ったものは重複させない
    e.sent = true;
    sendToServer({ ...e, tag: `ctx/${e.tag}` });
  });
};

export const getDebugLogs = (): DebugLogEntry[] => entries;

export const clearDebugLogs = () => {
  entries = [];
  persist();
  notify();
};

export const subscribeDebugLogs = (fn: () => void) => {
  listeners.push(fn);
  return () => {
    listeners = listeners.filter((x) => x !== fn);
  };
};

const safeStringify = (value: any): string => {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch (e) {
    return String(value);
  }
};

/*
 * 計測結果が「正常」と言い切れるかを判定する。
 *
 * 常設のログなので、正常な計測は送らず手元に残すだけにする。
 * ただし取りこぼすと調査できなくなるため、**正常と断定できる形だけ**を除外し、
 * 判断が付かないものはすべて異常として送る（安全側に倒す）。
 *
 *   正常1: 要素があり、高さがあり、画面内に見えている
 *   正常2: 要素が無く、読み込み中（データ待ちなので当然）
 *   正常3: 要素が無く、持っているデータも0件（表示すべきものが無い）
 */
const isHealthyMeasure = (info: Record<string, any>): boolean => {
  const count = Number(info.count ?? 0);
  const height = Number(info.height ?? 0);
  const visible = info.visible === true;
  const loading = info.loading === true;
  const stored = Number(info.stored ?? 0);

  if (count > 0 && height > 0 && visible) return true;
  if (count === 0 && loading) return true;
  if (count === 0 && stored === 0) return true;
  return false;
};

/**
 * 通知一覧などの「DOMには存在するが画面に見えない」を判定するための計測。
 * 計測自体は毎回おこなうが、**正常な結果はサーバーへ送らない**。
 *
 * キーを英数字にしているのは、URLエンコード時に日本語が3倍に膨らむのを避けるため。
 */
export const measureElement = (tag: string, selector: string, extra?: Record<string, any>) => {
  if (!isDebugEnabled()) return;
  const el = document.querySelector(selector) as HTMLElement | null;
  if (!el) {
    debugLog("error", tag, `notfound ${selector} ${extra ? safeStringify(extra) : ""}`);
    return;
  }
  const rect = el.getBoundingClientRect();
  const first = el.firstElementChild as HTMLElement | null;
  const firstRect = first ? first.getBoundingClientRect() : null;
  const info = {
    count: el.childElementCount,        // DOM上のカード数
    height: Math.round(rect.height),    // コンテナの高さ
    top: Math.round(rect.top),          // 画面上端からの位置
    visible: rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight,
    firstH: firstRect ? Math.round(firstRect.height) : null,
    firstTop: firstRect ? Math.round(firstRect.top) : null,
    scrollY: Math.round(window.scrollY),
    winH: window.innerHeight,
    ...(extra || {}),
  };

  const healthy = isHealthyMeasure(info);
  // 正常なら手元に残すだけ。異常なら前後関係ごとサーバーへ送る
  if (!healthy) sendRecentContext();
  debugLog(healthy ? "measure" : "error", tag, safeStringify(info), !healthy);
};

let installed = false;

/**
 * グローバルなエラー捕捉と console のフックを仕掛ける。index.tsx から一度だけ呼ぶ。
 */
export const installDebugLogger = () => {
  if (!isDebugEnabled() || installed) return;
  installed = true;

  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) entries = JSON.parse(saved);
  } catch (e) {
    entries = [];
  }

  window.addEventListener("error", (ev: ErrorEvent) => {
    debugLog("error", "window.onerror", `${ev.message} @ ${ev.filename}:${ev.lineno}:${ev.colno}`);
  });

  window.addEventListener("unhandledrejection", (ev: PromiseRejectionEvent) => {
    debugLog("error", "unhandledrejection", safeStringify(ev.reason));
  });

  const origError = console.error;
  console.error = (...args: any[]) => {
    debugLog("error", "console.error", args.map(safeStringify).join(" "));
    origError.apply(console, args);
  };

  const origWarn = console.warn;
  console.warn = (...args: any[]) => {
    debugLog("warn", "console.warn", args.map(safeStringify).join(" "));
    origWarn.apply(console, args);
  };

  debugLog("info", "起動", `${navigator.userAgent.slice(0, 120)}`);
  debugLog("info", "画面", `${window.innerWidth}x${window.innerHeight} dpr=${window.devicePixelRatio}`);
};
